import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { parseManifestText, errorWords } from "@alm/domain";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { useTableSort } from "@/components/app/lists/table-sort";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { saveFile } from "@/lib/files/download";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { optionalString, optionalTrue, KEEP_PLACE, optionalSearch } from "@/lib/search";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { useCan } from "@/lib/policy";
import { ManifestTab } from "./-components/manifest-tab";
import { ReceivingActions } from "./-components/receiving-actions";
import { count, RecordBar } from "./-components/record-bar";
import { SlipImportDialog } from "./-components/slip-import-dialog";
import { certainQuantities } from "./-components/slip-import-totals";
import type { Reconciliation } from "./-components/slip-import-types";
import { UnsavedNotice } from "./-components/unsaved-notice";
import { useScanCheck } from "./-components/use-scan-check";
import { WorksheetFilters } from "./-components/worksheet-filters";
import { WorksheetTable } from "./-components/worksheet-table";
import {
  filterWorksheet,
  prefill,
  type Entry,
  type WorksheetMode,
  type WorksheetSortKey,
} from "./-components/worksheet-types";

type Mode = "receive" | "count" | "reconcile";

const MODES: readonly Mode[] = ["receive", "count", "reconcile"];

type ReceivingSearch = { mode?: Mode; q?: string; pharmacy?: string; expected?: true };

export const Route = createFileRoute("/_authenticated/_pharmacy/pharmacy-receiving/")({
  /* The forecast sends somebody here to count what it cannot see, so the tab
     it means rides in the link rather than being a second thing to find. */
  validateSearch: (search: Record<string, unknown>): ReceivingSearch => ({
    mode: MODES.find((value) => value === search.mode),
    q: optionalSearch(search.q),
    pharmacy: optionalString(search.pharmacy)?.slice(0, 60) || undefined,
    expected: optionalTrue(search.expected),
  }),
  component: PharmacyReceiving,
});

const TITLE = "Receiving & counts";
const META = "Enter a whole delivery in one pass, or count the shelf and see the variance.";

const fail = (error: Error) => toast.error(errorWords(error));

/** One key per worksheet session: a resubmit must not double the inventory. */
const newRef = () => `wk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** The worksheet somebody carries to the delivery, and what they record from it. Three modes over
 * one table: receive a delivery, count the shelf and see the variance, or paste the pharmacy's
 * manifest and match it against the orders this building actually charts. */
function PharmacyReceiving() {
  const { facilityId } = useFacility();
  const canManage = useCan("medication:receive");
  const queryClient = useQueryClient();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ReceivingSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const mode: Mode = params.mode ?? "receive";
  const setMode = (next: Mode) => patch({ mode: next === "receive" ? undefined : next });
  const pharmacyId = params.pharmacy ?? "all";
  const setPharmacyId = (next: string) => patch({ pharmacy: next === "all" ? undefined : next });
  const expectedOnly = Boolean(params.expected);
  const setExpectedOnly = (next: boolean) => patch({ expected: next ? true : undefined });
  const { text: search, change: setSearch } = useSearchParamText(params.q, (q) => patch({ q }));
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [note, setNote] = useState("");
  const [deliveryRef, setDeliveryRef] = useState(newRef);
  const [manifest, setManifest] = useState("");
  const [reconciled, setReconciled] = useState<Reconciliation | null>(null);
  const [importing, setImporting] = useState(false);
  /* One order for both worksheets: the tab is a way of reading the sheet, not
     a different sheet. */
  const sort = useTableSort<WorksheetSortKey>();
  const [unsaved, setUnsaved] = useState<string[]>([]);

  const realFacility = facilityId === "all" ? null : facilityId;
  const sheet = useQuery({
    ...orpc.pharmacy.receiving.worksheet.queryOptions({
      input: {
        facilityId: realFacility ?? "",
        expectedOnly,
        ...(pharmacyId === "all" ? {} : { pharmacyId }),
      },
    }),
    enabled: Boolean(realFacility),
  });
  const items = useMemo(() => sheet.data?.items ?? [], [sheet.data]);
  const scan = useScanCheck(items);

  const download = useMutation(
    orpc.pharmacy.receiving.worksheetExport.mutationOptions({ onSuccess: saveFile, onError: fail }),
  );

  const apply = useMutation(
    orpc.pharmacy.receiving.apply.mutationOptions({
      onSuccess: async (result, variables) => {
        if (result.duplicateOf) {
          toast.info("That delivery was already recorded.");
          return;
        }
        // An order that vanished between the worksheet loading and the save is
        // skipped rather than failing the batch, so what it typed is kept.
        const saved = new Set(result.applied.map((row) => row.orderId));
        const missed = variables.entries
          .map((entry) => entry.orderId)
          .filter((orderId) => !saved.has(orderId));
        setUnsaved(missed);
        setEntries((previous) =>
          Object.fromEntries(
            Object.entries(previous).filter(([orderId]) => missed.includes(orderId)),
          ),
        );
        if (missed.length) toast.error(`${count(missed.length, "row")} did not save`);
        else toast.success(`Recorded ${count(result.applied.length, "item")}`);
        setNote("");
        setDeliveryRef(newRef());
        await invalidateKeys(queryClient, [orpc.pharmacy.key(), orpc.care.residents.orders.key()]);
      },
      onError: fail,
    }),
  );

  const match = useMutation(
    orpc.pharmacy.receiving.reconcile.mutationOptions({
      onSuccess: (result) => {
        setReconciled(result);
        // Pre-fill from every confident match: the point is to save typing,
        // but only where the match is unambiguous.
        setEntries((current) => prefill(current, certainQuantities(result)));
        if (result.matched.length === 0) toast.info("No lines matched an active order");
        else toast.success(`${count(result.matched.length, "line")} matched`);
      },
      onError: fail,
    }),
  );

  const rows = useMemo(() => filterWorksheet(items, search), [items, search]);

  const typeInto = (orderId: string, change: Partial<Entry>) =>
    setEntries((previous) => ({
      ...previous,
      [orderId]: {
        qty: previous[orderId]?.qty ?? "",
        short: previous[orderId]?.short ?? false,
        ...change,
      },
    }));

  const filled = Object.entries(entries).filter(
    ([, entry]) => entry.qty.trim() !== "" && Number.isFinite(Number(entry.qty)),
  );

  const record = () => {
    if (!realFacility) return;
    apply.mutate({
      facilityId: realFacility,
      entries: filled.map(([orderId, entry]) => ({
        orderId,
        kind: mode === "count" ? "count_set" : entry.short ? "received_short" : "received_full",
        quantity: Number(entry.qty),
        note: note.trim() || null,
      })),
      deliveryRef,
    });
  };

  if (!realFacility) {
    return (
      <>
        <PageHeader title={TITLE} meta={META} />
        <OneBuildingCard prompt="Choose a building. A delivery arrives at one door, so receiving and counts run for one building at a time." />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={TITLE}
        meta={META}
        actions={
          <ReceivingActions
            canManage={canManage}
            onImport={() => setImporting(true)}
            exporting={download.isPending}
            onExport={(format) =>
              download.mutate({ facilityId: realFacility, expectedOnly, format })
            }
          />
        }
      />

      <Tabs value={mode} onValueChange={(value) => setMode(value as typeof mode)}>
        <TabsList>
          <TabsTrigger value="receive">Receive a delivery</TabsTrigger>
          <TabsTrigger value="count">Physical count</TabsTrigger>
          <TabsTrigger value="reconcile">Match a manifest</TabsTrigger>
        </TabsList>

        <TabsContent value="reconcile" className="mt-3">
          <ManifestTab
            text={manifest}
            onText={setManifest}
            pending={match.isPending}
            reconciled={reconciled}
            onMatch={() => {
              const lines = parseManifestText(manifest);
              if (lines.length === 0) {
                toast.error(
                  "Couldn't read any lines from that text. Paste the manifest one item per line.",
                );
                return;
              }
              match.mutate({ facilityId: realFacility, lines });
            }}
          />
        </TabsContent>

        {(["receive", "count"] as WorksheetMode[]).map((tab) => (
          <TabsContent key={tab} value={tab} className="mt-3 space-y-3">
            <WorksheetFilters
              pharmacies={sheet.data?.pharmacies ?? []}
              pharmacyId={pharmacyId}
              onPharmacy={setPharmacyId}
              expectedOnly={expectedOnly}
              onExpectedOnly={setExpectedOnly}
              search={search}
              onSearch={setSearch}
              scan={scan.scan}
              onScan={scan.setScan}
              onScanSubmit={scan.check}
              verified={scan.verified.size}
              unknownScans={scan.unknownScans}
            />

            <UnsavedNotice orderIds={unsaved} items={items} />

            {sheet.isPending ? (
              <Skeleton className="h-64 w-full" />
            ) : sheet.isError ? (
              <ReadFailed
                className="bg-card rounded-xl border p-4"
                message="Couldn't load the worksheet, so there is nothing to enter against."
                error={sheet.error}
                retrying={sheet.isFetching}
                onRetry={() => void sheet.refetch()}
              />
            ) : (
              <WorksheetTable
                mode={tab}
                rows={rows}
                entries={entries}
                verified={scan.verified}
                canManage={canManage}
                sort={sort}
                onQuantity={(orderId, qty) => typeInto(orderId, { qty })}
                onShort={(orderId, short) => typeInto(orderId, { short })}
              />
            )}

            {canManage ? (
              <RecordBar
                mode={tab}
                entered={filled.length}
                note={note}
                onNote={setNote}
                pending={apply.isPending}
                onRecord={record}
              />
            ) : null}
          </TabsContent>
        ))}
      </Tabs>

      {importing ? (
        <SlipImportDialog
          open
          onOpenChange={setImporting}
          facilityId={realFacility}
          rows={items}
          onApply={(result) => {
            // Nothing is written until a person presses Receive — the
            // worksheet IS the review.
            setEntries((current) => prefill(current, result.quantities));
            setReconciled(result.reconciled);
            setMode("receive");
            toast.success(
              `${count(Object.keys(result.quantities).length, "row")} prefilled from the slip`,
              {
                description: "Check the quantities (scan-check helps), then press Receive.",
              },
            );
          }}
        />
      ) : null}
    </>
  );
}
